import { PenIcon, TrashIcon } from "assets/icons";
import cn from "classnames";
import {
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  MAX_IMAGE_FILE_SIZE,
  MAX_VIDEO_FILE_SIZE,
  MAX_DOCUMENT_FILE_SIZE,
  DOCUMENT_FORMATS,
  BYTES_IN_MB
} from "constants/common";
import { MediaType } from "constants/enums";
import { getAcceptObjectForFilesInput, handleUploadFileError } from "helpers";
import { useImagesCompressor, useVideoCompressor } from "hooks";
import { t } from "i18next";
import { FileRejection, useDropzone } from "react-dropzone";
import { ClipLoader } from "react-spinners";

import { MediaFilePickerProps } from "./MediaFilePickerAlternative.types";

import styles from "./MediaFilePickerAlternative.module.scss";

const MediaFilePickerAlternative = ({
  allowedMediaTypes = [MediaType.IMAGE, MediaType.VIDEO],
  size,
  value,
  placeholder,
  acceptImageFormats = IMAGE_FORMATS,
  acceptVideoFormats = VIDEO_FORMATS,
  acceptDocumentFormats = DOCUMENT_FORMATS,
  loadingFilesCounter,
  isLoading,
  withCompression = true,
  customMaxFileSizeInMB,
  compressionOptions,
  onDeleteMediaFile,
  onFilesDrop
}: MediaFilePickerProps) => {
  const isImagesAllowed = allowedMediaTypes.includes(MediaType.IMAGE);
  const isVideosAllowed = allowedMediaTypes.includes(MediaType.VIDEO);
  const isDocumentsAllowed = allowedMediaTypes.includes(MediaType.DOCUMENT);

  const { compressImages, compressingImagesCount } =
    useImagesCompressor(compressionOptions);
  const {
    compressVideos,
    status: videoStatus,
    progress: videoProgress
  } = useVideoCompressor();
  const isProcessingVideo = videoStatus !== "idle";

  const onDropImageRejected = (files: FileRejection[]) => {
    const file = files[0];
    const message = file.errors[0].message;
    const code = file.errors[0].code;

    handleUploadFileError(
      message,
      code,
      allowedMediaTypes,
      customMaxFileSizeInMB
    );
  };

  const handleDropAccepted = async (files: File[]) => {
    if (withCompression) {
      const compressed = await compressImages(files);
      const processed = await compressVideos(compressed);
      onFilesDrop(processed);
    } else {
      onFilesDrop(files);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: getAcceptObjectForFilesInput({
      acceptImageFormats,
      acceptVideoFormats,
      acceptDocumentFormats,
      isImagesAllowed,
      isVideosAllowed,
      isDocumentsAllowed
    }),
    maxSize: customMaxFileSizeInMB
      ? customMaxFileSizeInMB * BYTES_IN_MB
      : allowedMediaTypes.includes(MediaType.VIDEO)
        ? MAX_VIDEO_FILE_SIZE
        : allowedMediaTypes.includes(MediaType.IMAGE)
          ? MAX_IMAGE_FILE_SIZE
          : MAX_DOCUMENT_FILE_SIZE,
    maxFiles: 1,
    noDrag: true,
    noKeyboard: true,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onDropAccepted: handleDropAccepted,
    onDropRejected: onDropImageRejected
  });

  return (
    <div className={styles.piker}>
      <div
        className={styles.piker_mediaContainer}
        style={{
          width: size.width === "full" ? "100%" : `${size.width}px`,
          height: size.height,
          borderRadius: size.borderRadius
        }}
      >
        {loadingFilesCounter > 0 ||
        compressingImagesCount > 0 ||
        isProcessingVideo ||
        isLoading ? (
          <>
            {isProcessingVideo ? (
              <div style={{ width: "80%", textAlign: "center" }}>
                <p
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#FB3973",
                    marginBottom: 6
                  }}
                >
                  {videoStatus === "loading"
                    ? "Preparing..."
                    : `Optimizing video... ${videoProgress}%`}
                </p>
                <div
                  style={{
                    width: "100%",
                    height: 6,
                    borderRadius: 6,
                    backgroundColor: "#F6F6FA"
                  }}
                >
                  <div
                    style={{
                      width: `${videoProgress}%`,
                      height: "100%",
                      borderRadius: 6,
                      backgroundColor: "#FB3973",
                      transition: "width 0.3s"
                    }}
                  />
                </div>
              </div>
            ) : (
              <ClipLoader size={20} color="#FB3973" />
            )}
          </>
        ) : (
          <>
            {value?.mediaType === MediaType.VIDEO ? (
              <video className={styles.piker_image} src={value.url} controls />
            ) : (value?.url ?? placeholder) ? (
              <img
                className={styles.piker_image}
                src={value?.url ?? placeholder}
              />
            ) : (
              <div className={styles.piker_image} />
            )}

            <input {...getInputProps()} style={{ display: "none" }} />
          </>
        )}
      </div>

      <div className={styles.piker_buttonsContainer}>
        <button
          {...getRootProps()}
          type="button"
          className={styles.piker_button}
        >
          <PenIcon width={14} height={14} />
          {t("Change image")}
        </button>

        {value && (
          <button
            type="button"
            className={cn(styles.piker_button, styles.piker_button__delete)}
            onClick={onDeleteMediaFile}
          >
            <TrashIcon width={14} height={14} />
            {t("Delete image")}
          </button>
        )}
      </div>
    </div>
  );
};

export default MediaFilePickerAlternative;
